import { useState, useRef, useCallback, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { UploadCloud, ImagePlus, Sparkles, AlertTriangle, ShieldCheck } from "lucide-react";
import BiometricDisclosureModal from "@/components/BiometricDisclosureModal";

const ALLOWED = ["image/jpeg", "image/png", "image/webp"];

// A stable per-tab anonymous session id — purely for legal audit; not
// considered personally identifying on its own.
function getSessionId() {
  try {
    let id = sessionStorage.getItem("hm_session_id");
    if (!id) {
      id = (window.crypto?.randomUUID?.() ||
        Math.random().toString(36).slice(2) + Date.now().toString(36));
      sessionStorage.setItem("hm_session_id", id);
    }
    return id;
  } catch {
    return Math.random().toString(36).slice(2) + Date.now().toString(36);
  }
}

export default function Scan() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [drag, setDrag] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [consent, setConsent] = useState(false); // UNCHECKED by default
  const [savePhoto, setSavePhoto] = useState(false); // optional opt-in to keep photo
  const [consentText, setConsentText] = useState(
    "I consent to HairMaxxing Pro analyzing my facial geometry from this photo to generate hairstyle recommendations. I have read the Privacy Policy."
  );
  // State-specific BIPA / CUBI / WA disclosure gating
  const [jurisdictionState, setJurisdictionState] = useState(null);
  const [requiresExtraDisclosure, setRequiresExtraDisclosure] = useState(false);
  const [stateLawAcknowledged, setStateLawAcknowledged] = useState(null); // statute string once acknowledged
  const [modalOpen, setModalOpen] = useState(false);
  const inputRef = useRef(null);

  // Fetch the canonical consent text from the server (so it always matches
  // what's recorded). Falls back to the local literal if the network call
  // fails. Also fetch the jurisdiction to know if we need the extra modal.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/consent/current");
        if (data?.text) setConsentText(data.text);
      } catch {/* ignore — fall back to local copy */}
      try {
        const { data } = await api.get("/consent/jurisdiction");
        if (data?.state) setJurisdictionState(data.state);
        if (data?.requires_extra_disclosure) setRequiresExtraDisclosure(true);
      } catch {/* ignore — fall back to standard consent only */}
    })();
  }, []);

  const accept = useCallback((f) => {
    if (!f) return;
    if (!ALLOWED.includes(f.type)) {
      setError("Only JPG, PNG, or WEBP images are allowed.");
      return;
    }
    if (f.size > 10 * 1024 * 1024) {
      setError("Image must be under 10 MB.");
      return;
    }
    setError("");
    setFile(f);
    setPreview(URL.createObjectURL(f));
  }, []);

  const onDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    accept(e.dataTransfer.files?.[0]);
  };

  const submit = async () => {
    if (!file) return;
    if (!consent) {
      setError("Please review and accept the biometric privacy consent before analyzing.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      // Step 1: record a single-use consent record on the server.
      const { data: cr } = await api.post("/consent", {
        session_id: getSessionId(),
        state_law_acknowledged: stateLawAcknowledged,
      });
      // Step 2: submit the scan with the consent_id attached.
      const fd = new FormData();
      fd.append("file", file);
      fd.append("consent_id", cr.consent_id);
      fd.append("save_photo", savePhoto ? "true" : "false");
      const { data } = await api.post("/scans", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await refresh();
      navigate(`/scan/${data.id}`, { state: { scan: data } });
    } catch (e) {
      const detail = e.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Couldn't analyze that image. Try a different photo.");
    } finally {
      setBusy(false);
    }
  };

  // When the user clicks the main consent checkbox and they're in a strict
  // state but haven't yet acknowledged the modal, open the modal instead of
  // toggling.
  const handleConsentToggle = (nextChecked) => {
    if (nextChecked && requiresExtraDisclosure && !stateLawAcknowledged) {
      setModalOpen(true);
      return;
    }
    setConsent(nextChecked);
  };

  const handleModalAccept = (statute) => {
    setStateLawAcknowledged(statute);
    setRequiresExtraDisclosure(false); // gate cleared for the rest of this session
    setModalOpen(false);
    setConsent(true);
  };

  const freeRemaining = user?.scans_remaining_today ?? user?.daily_limit ?? 5;
  const bonus = user?.bonus_scans ?? 0;
  const credits = freeRemaining + bonus;
  const outOfCredits = credits <= 0;
  const submitDisabled = !file || busy || outOfCredits || !consent;

  return (
    <div className="max-w-4xl mx-auto px-6 md:px-10 py-12 md:py-16 animate-fade-in">
      <p className="cm-label">NEW SCAN</p>
      <h1 className="cm-heading text-4xl sm:text-5xl mt-3" data-testid="scan-page-heading">
        Drop the <span className="text-copper">photo</span>.
      </h1>
      <p className="mt-3 text-gray-400 max-w-xl">
        JPG, PNG, or WEBP — max 10 MB. Use a clear shot of the head, ideally from the front or side.
      </p>

      {outOfCredits && (
        <div className="mt-8 cm-card p-6 border-copper/40">
          <p className="cm-label">SCANS USED UP</p>
          <p className="mt-3 text-gray-300">
            You&rsquo;ve used your {user?.daily_limit ?? 5} free scans for the last {user?.window_hours ?? 12} hours and your top-up balance is empty. Grab a 5-scan pack for $2.99 — scans never expire.
          </p>
          <button
            onClick={() => navigate("/buy-credits")}
            className="cm-btn-primary mt-4"
            data-testid="scan-buy-credits-cta"
          >
            Buy 5 scans
          </button>
        </div>
      )}

      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDrag(true);
        }}
        onDragLeave={() => setDrag(false)}
        onDrop={onDrop}
        onClick={() => !busy && inputRef.current?.click()}
        className={`cm-dropzone mt-10 p-10 md:p-14 flex flex-col items-center justify-center text-center cursor-pointer ${drag ? "is-drag" : ""} ${outOfCredits ? "opacity-50 pointer-events-none" : ""}`}
        data-testid="scan-dropzone"
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={(e) => accept(e.target.files?.[0])}
          className="hidden"
          data-testid="scan-file-input"
          aria-label="Upload haircut photo"
        />
        {preview ? (
          <img src={preview} alt="Selected haircut preview" className="max-h-80 rounded-xl object-contain" />
        ) : (
          <>
            <UploadCloud className="w-10 h-10 text-copper mb-4" aria-hidden="true" />
            <p className="cm-heading text-2xl">Drop your image here</p>
            <p className="text-sm text-gray-400 mt-3">or click to pick from your device</p>
          </>
        )}
      </div>

      {file && (
        <div className="mt-6 flex items-center justify-between cm-card p-4">
          <div className="flex items-center gap-3">
            <ImagePlus className="w-5 h-5 text-copper" aria-hidden="true" />
            <div>
              <p className="text-sm font-semibold truncate max-w-[16rem]">{file.name}</p>
              <p className="text-xs text-gray-500">{(file.size / 1024).toFixed(0)} KB</p>
            </div>
          </div>
          <button
            onClick={() => {
              setFile(null);
              setPreview(null);
            }}
            className="cm-btn-secondary text-xs"
            data-testid="scan-clear-button"
          >
            Clear
          </button>
        </div>
      )}

      {/* Biometric consent gate */}
      <div className="mt-6 cm-card p-5 border-copper/30" data-testid="consent-card">
        {requiresExtraDisclosure && (
          <button
            type="button"
            onClick={() => setModalOpen(true)}
            className="mb-4 w-full text-left p-3 rounded-md border border-copper/50 bg-copper/5 hover:bg-copper/10 transition-colors"
            data-testid="state-disclosure-banner"
          >
            <p className="cm-label text-[10px] text-copper">{jurisdictionState} STATE LAW NOTICE</p>
            <p className="text-sm text-platinum mt-1">
              Tap to read your state&rsquo;s required biometric privacy disclosure before consenting.
            </p>
          </button>
        )}
        {stateLawAcknowledged && (
          <p
            className="mb-3 text-[11px] text-copper/80 font-mono"
            data-testid="state-acknowledged-flag"
          >
            ✓ {stateLawAcknowledged} disclosure acknowledged
          </p>
        )}
        <div className="flex items-start gap-3">
          <ShieldCheck className="w-5 h-5 text-copper mt-0.5 flex-shrink-0" aria-hidden="true" />
          <div className="flex-1">
            <p className="cm-label text-[10px]">BIOMETRIC PRIVACY CONSENT</p>
            <label
              htmlFor="biometric-consent"
              className="mt-3 flex items-start gap-3 cursor-pointer select-none"
            >
              <input
                id="biometric-consent"
                type="checkbox"
                checked={consent}
                onChange={(e) => handleConsentToggle(e.target.checked)}
                className="mt-1 w-4 h-4 accent-copper cursor-pointer"
                data-testid="consent-checkbox"
                aria-describedby="consent-text"
              />
              <span id="consent-text" className="text-sm text-gray-300 leading-relaxed">
                I consent to HairMaxxing Pro analyzing my facial geometry from this
                photo to generate hairstyle recommendations. I have read the{" "}
                <Link
                  to="/privacy"
                  className="text-copper underline hover:text-copper/80"
                  data-testid="consent-privacy-link"
                  onClick={(e) => e.stopPropagation()}
                >
                  Privacy Policy
                </Link>
                .
              </span>
            </label>
            <p className="mt-3 text-[11px] text-gray-500 leading-relaxed">
              By default, we delete your photo and any derived facial-geometry
              data immediately after your results are generated.
              {consentText.length > 200 ? null : null}
            </p>
          </div>
        </div>
      </div>

      {/* Optional: save photo to account (separate, explicit opt-in) */}
      <div className="mt-4 cm-card p-5" data-testid="save-photo-card">
        <label
          htmlFor="save-photo-checkbox"
          className="flex items-start gap-3 cursor-pointer select-none"
        >
          <input
            id="save-photo-checkbox"
            type="checkbox"
            checked={savePhoto}
            onChange={(e) => setSavePhoto(e.target.checked)}
            className="mt-1 w-4 h-4 accent-copper cursor-pointer"
            data-testid="save-photo-checkbox"
            aria-describedby="save-photo-text"
          />
          <span id="save-photo-text" className="flex-1">
            <span className="cm-label text-[10px] block">SAVE PHOTO TO MY ACCOUNT (OPTIONAL)</span>
            <span className="text-sm text-gray-300 mt-2 block leading-relaxed">
              Keep this photo in my History so I can see it from any device
              I&rsquo;m logged into. I can delete it any time from History.
            </span>
            <span className="text-[11px] text-gray-500 mt-2 block leading-relaxed">
              We still never extract or store any biometric identifiers —
              only the raw photo, tied to your account.
            </span>
          </span>
        </label>
      </div>

      {error && (
        <div
          role="alert"
          aria-live="assertive"
          className="mt-6 flex items-center gap-2 text-red-400 text-sm"
          data-testid="scan-error"
        >
          <AlertTriangle className="w-4 h-4" aria-hidden="true" /> {error}
        </div>
      )}

      <button
        onClick={submit}
        disabled={submitDisabled}
        className="cm-btn-primary mt-8 inline-flex items-center gap-3"
        data-testid="scan-submit-button"
        aria-disabled={submitDisabled}
      >
        <Sparkles className="w-4 h-4" aria-hidden="true" />
        {busy ? "Analyzing…" : "Analyze haircut"}
      </button>
      <p className="mt-3 text-xs text-gray-500">
        Uses your free {user?.daily_limit ?? 5}/{user?.window_hours ?? 12}h allowance
        first, then your top-up balance.
      </p>

      <BiometricDisclosureModal
        state={jurisdictionState}
        open={modalOpen}
        onAcknowledge={handleModalAccept}
        onClose={() => setModalOpen(false)}
      />
    </div>
  );
}
